import React from 'react';
import { Box, Paper, SxProps, Theme } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { dataGridSx } from '../utils/dataGridStyles';

interface StyledDataGridProps {
  rows: any[];
  columns: GridColDef[];
  loading?: boolean;
  getRowId?: (row: any) => any;
  totalRows?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  rowHeight?: number;
  disableRowSelectionOnClick?: boolean;
  paginationMode?: 'client' | 'server';
  onRowClick?: (params: GridRowParams) => void;
  onRowDoubleClick?: (params: GridRowParams) => void;
  sx?: SxProps<Theme>;
}

const StyledDataGrid: React.FC<StyledDataGridProps> = ({
  rows,
  columns,
  loading = false,
  getRowId,
  totalRows,
  page = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  rowHeight = 60,
  // Default to false so clicking a row visibly selects it (the shared sx has a
  // Mui-selected highlight). Callers can still pass `true` to opt out.
  disableRowSelectionOnClick = false,
  paginationMode = 'server',
  onRowClick,
  onRowDoubleClick,
  sx: customSx
}) => {
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    if (onPageChange && model.page !== page - 1) {
      onPageChange(model.page + 1);
    }
    if (onPageSizeChange && model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
      <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          loading={loading}
          pagination
          paginationMode={paginationMode}
          rowCount={totalRows || rows.length}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          rowHeight={rowHeight}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          {...(onRowClick && { onRowClick })}
          {...(onRowDoubleClick && { onRowDoubleClick })}
          // sx is passed as an array so a caller's `customSx` (e.g. a
          // `'& .MuiDataGrid-row'` cursor override) merges with the shared
          // dataGridSx instead of replacing that whole key — keeping the
          // alternating stripes and the selected-row highlight intact.
          sx={[
            dataGridSx,
            {
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50'
              }
            },
            ...(Array.isArray(customSx) ? customSx : customSx ? [customSx] : [])
          ] as any}
        />
      </Paper>
    </Box>
  );
};

export default StyledDataGrid;
